import { Component, computed, effect, forwardRef, inject, input, output, signal, untracked } from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../core/utils";
import { PageMode } from "../../../constants/enums";
import type { ParameterCatalog } from "../../../models";
import { FormulasApiService } from "../../../services";

@Component({
  selector: "adaa-form-input-formula",
  imports: [ReactiveFormsModule, TranslatePipe],
  templateUrl: "./form-input-formula.html",
  styleUrl: "./form-input-formula.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormInputFormulaComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormInputFormulaComponent),
    },
  ],
})
export class FormInputFormulaComponent implements ControlValueAccessor {
  readonly inputControl = new FormControl();
  readonly AdaaHelper = AdaaHelper;
  readonly PageMode = PageMode;

  private readonly _formulasTemplateService = inject(FormulasApiService);
  private readonly _requiredFromValidator = signal<boolean>(false);
  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  private _onChange: (value: string) => void;

  readonly required = input<boolean>(false);
  readonly pageMode = input<string>("");
  readonly invalid = input<boolean>(false);
  readonly label = input<string>("");
  readonly placeholder = input<string>("");
  readonly isDisabled = input<boolean>(false);
  readonly maxLength = input<number | null>(null);
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly rows = input<number>(5);
  readonly info = input<string>();
  readonly setDefaultValue = input<string>();
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly inputChanges = output<any>();
  readonly isFormulaValid = output<boolean | null>();

  output: string;
  correct: boolean | null;

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());

  constructor() {
    effect(() => {
      const value = this.setDefaultValue();
      if (value === undefined) return;
      untracked(() => {
        if (value) this.writeValue(value);
      });
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          untracked(() => {
            this._requiredFromValidator.set(field.mandatory === "Y");
          });
        }
      }
    });
  }

  public writeValue(obj: string | number): void {
    if (obj === null) {
      this.inputControl.reset();
    }

    this.inputControl.setValue(obj);
  }

  public registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  public validate(_value: FormControl): false | { required: boolean } {
    const isRequired =
      (this.isRequired() && this.inputControl.value === null) || (this.isRequired() && this.inputControl.value === "");
    return (
      isRequired && {
        required: true,
      }
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public valueChanged(event: any): void {
    if (this._onChange) this._onChange(event);

    this.correct = null;
    this.isFormulaValid.emit(this.correct);

    this.output = event;
    this.inputChanges.emit(event);
  }

  public validateFormula() {
    this._formulasTemplateService.validateFormula({ formula: this.output }).subscribe({
      next: (response) => {
        this.correct = response.responseCode === "1";
        this.isFormulaValid.emit(this.correct);
      },
    });
  }
}
